import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SlideModal, type SlideModalHandler } from 'src/components/shared/modal/SlideModal';
import { useModal } from 'src/hooks/useModal';
import { useShopChange } from 'src/hooks/useShopChange';
import type { ShopSchema } from 'src/models/shop/schema';
import { ShopsContext } from 'src/stores/Shops';
import { mixpanel } from 'src/utils/mixpanel';
import { DetailContent } from './DetailContent';
import { ListContent } from './ListContent';
import { ShopContentSwitcher } from './ShopContentSwitcher';
import { ShopModalTitle } from './ShopModalTitle';
import { ShopNavigation } from './ShopNavigation';
import { VJWContent } from './VJWContent';
import { type ShopModalMode } from './type';

export const ShopSelectModalNew = () => {
  const { isVisible, setIsVisible } = useModal('shopSelectModal');
  const shops = useContext(ShopsContext);
  const modalRef = useRef<SlideModalHandler>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const detailContainerRef = useRef<HTMLDivElement>(null);
  const vjwContainerRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<ShopModalMode>('list');
  const [selectedShop, setSelectedShop] = useState<ShopSchema>();
  const [vjwChecked, setVjwChecked] = useState(false);
  const { onShopChange, currentShopId, setCurrentShopId } = useShopChange();

  useEffect(() => {
    if (selectedShop && selectedShop.id !== currentShopId) {
      setCurrentShopId(selectedShop.id);
      setVjwChecked(false);
    }
  }, [currentShopId, selectedShop, setCurrentShopId]);

  const shopsData = useMemo(() => {
    const map = new Map<string, ShopSchema[]>();
    for (const shop of shops) {
      if (shop.prefectureName) {
        map.set(shop.prefectureName, [...(map.get(shop.prefectureName) ?? []), shop]);
      }
    }
    return { map, prefectures: Array.from(map.keys()) };
  }, [shops]);

  const backToTop = () => {
    listContainerRef.current?.scrollTo({ top: 0 });
    detailContainerRef.current?.scrollTo({ top: 0 });
    vjwContainerRef.current?.scrollTo({ top: 0 });
  };

  const onCloseModal = () => {
    mixpanel.onShopModalClose();
    setIsVisible(false);
    setMode('list');
    backToTop();
  };

  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (prevModeRef.current !== mode) {
      backToTop();
    }
    prevModeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    if (isVisible) {
      modalRef.current?.open?.();
    } else {
      modalRef.current?.close?.();
    }
  }, [isVisible]);

  return (
    <SlideModal ref={modalRef} withDefaultLayout={false} onCloseModal={onCloseModal}>
      <ShopModalTitle mode={mode} shop={selectedShop} />
      <ShopContentSwitcher mode={mode}>
        <ListContent
          shopsData={shopsData}
          setMode={setMode}
          setSelectedShop={setSelectedShop}
          containerRef={listContainerRef}
        />
        <DetailContent selectedShop={selectedShop} containerRef={detailContainerRef} />
        <VJWContent containerRef={vjwContainerRef} />
      </ShopContentSwitcher>
      <ShopNavigation
        mode={mode}
        setMode={setMode}
        closeModal={modalRef.current?.close}
        selectedShop={selectedShop}
        vjwChecked={vjwChecked}
        onShopChange={onShopChange}
        checked={vjwChecked}
        setChecked={setVjwChecked}
      />
    </SlideModal>
  );
};
